import { useEffect } from 'react';

// Hook to set up token management event listeners and periodic checks
export const useTokenManagement = () => {
  useEffect(() => {
    // Listen for storage changes across tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'access_token' && !e.newValue) {
        // Token was cleared, redirect to login if we're not already there
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Set up periodic token refresh check (every 5 minutes)
    const checkTokenExpiry = () => {
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (accessToken && refreshToken) {
        try {
          // Decode JWT to check expiration (basic check)
          const payload = JSON.parse(atob(accessToken.split('.')[1]));
          const expirationTime = payload.exp * 1000; // Convert to milliseconds
          const currentTime = Date.now();
          const fiveMinutes = 5 * 60 * 1000;
          
          // If token expires in next 5 minutes, it will be refreshed on next API call
          // This is just for monitoring - the API client handles the actual refresh
          if (expirationTime - currentTime < fiveMinutes) {
            console.log('Access token will expire soon, will refresh on next API call');
          }
        } catch (error) {
          console.error('Error checking token expiry:', error);
        }
      }
    };

    const intervalId = setInterval(checkTokenExpiry, 5 * 60 * 1000); // Check every 5 minutes

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, []);
};

// Utility function to check if access token is expired
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expirationTime = payload.exp * 1000;
    return Date.now() >= expirationTime;
  } catch (error) {
    return true; // Assume expired if we can't parse
  }
};

// Utility function to get time until token expires
export const getTokenTimeToExpiry = (token: string): number => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expirationTime = payload.exp * 1000;
    return Math.max(0, expirationTime - Date.now());
  } catch (error) {
    return 0;
  }
};